import {
  Component,
  inject,
  ViewChild,
  OnInit,
  AfterViewInit,
  OnDestroy,
  output,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatPaginatorModule,
  type PageEvent,
} from '@angular/material/paginator';
import type { Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import debounce from 'lodash/debounce';

import { QgisService } from '../../../qgis.service';
import {
  DatasafraSearchReportsComponent,
  FilterFieldConfig,
  ReportFilters,
} from '../../../shared/components/datasafra-search-reports/datasafra-search-reports.component';
import { EnhancedLoadingService } from '../../../shared/services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { isAbortOrAbortCause } from '../../../shared/utils/is-abort-or-abort-cause';
import { ComplianceReportsFilters } from './../../../shared/types/compliances-report.types';
import { filterConfig } from './filterConfig';
import { tableColumns } from './tableColumns';
import { GeographicDataService } from '../../../shared/services/geographic-data.service';
import { UserService } from '../../../shared/services/user.service';
import { createNestedOrder } from '../../../shared/utils/create-nested-order';
import type { Compliance } from '#backend/entity/loaded/compliance/Compliance';

import { SnackbarService } from '../../../shared/services/snackbar.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FeatureFlagsService } from '../../../shared/services/feature-flags.service';
import formatArea from '../../../shared/utils/formate-number';
import {
  ReportsTableExpandComponent,
  ReportTableAction,
  ReportTableColumn,
  ReportTableConfig,
  ReportTableItem,
} from '../../../shared/components/reports-table-expand/reports-table-expand.component';
import { MatDialog } from '@angular/material/dialog';
import { ComplianceResultListDialog } from '../../../shared/components/compliance-result-list-dialog/compliance-result-list-dialog.component';
import type { StatusConformidade } from '#backend/entity/loaded/compliance/StatusConformidade';

@Component({
  selector: 'app-compliances-reports',
  imports: [
    AsyncPipe,
    DatasafraSearchReportsComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTableModule,
    ReportsTableExpandComponent,
  ],
  templateUrl: './compliances-reports.component.html',
  styleUrl: './compliances-reports.component.scss',
})
export class CompliancesReportsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly dialog = inject(MatDialog);

  @ViewChild(DatasafraSearchReportsComponent)
  declare searchComponent: DatasafraSearchReportsComponent;

  @ViewChild(ReportsTableExpandComponent)
  declare tableComponent: ReportsTableExpandComponent;

  private smartClient = inject(SmartOrpcClientService);
  loadingService = inject(EnhancedLoadingService);
  private qgis = inject(QgisService);
  geographicDataService = inject(GeographicDataService);
  userService = inject(UserService);
  private snackBar = inject(MatSnackBar);
  private snackBarService = inject(SnackbarService);
  private route = inject(ActivatedRoute);
  private featureFlags = inject(FeatureFlagsService);

  @ViewChild(ReportsTableExpandComponent) table!: ReportsTableExpandComponent;

  gridLoading$ = this.loadingService.grid$;

  complianceRequestId: number | null = null;

  dataSource = new MatTableDataSource<ReportTableItem>([]);

  private currentPageIndex = 0;
  private currentPageSize = 5;

  length = 0;

  private currentFilters: ComplianceReportsFilters = {};
  private currentSort: Sort = {
    active: 'batchComplianceRequest.createdDate',
    direction: 'desc',
  };

  abortController?: AbortController;

  // Filtrar campos baseado em feature flags
  filterConfig: FilterFieldConfig<ComplianceReportsFilters>[] = filterConfig();

  // Filtrar colunas baseado em feature flags
  tableColumns: ReportTableColumn[] = tableColumns();

  tableActions: ReportTableAction[] = [
    {
      key: 'expand',
      label: 'Expandir',
      icon: 'expand_more',
      action: (item) => this.table.toggle(item),
    },
    {
      key: 'download',
      label: 'Baixar',
      icon: 'file_download',
      action: (item) => {
        this.qgis.downloadComplianceReport(item.id);
      },
      condition: (item) => item.areaStatus?.code !== 'NÃO ANALISADO',
    },
    {
      key: 'view',
      label: 'Visualizar',
      icon: 'visibility',
      action: (item) => {
        this.qgis.openFileInNewTab(item.id);
      },
      condition: (item) => item.areaStatus?.code !== 'NÃO ANALISADO',
    },
  ];

  expandedElement: ReportTableItem | null = null;

  tableConfig: ReportTableConfig = {
    showActionsColumn: false,
    columns: this.tableColumns,
    initialSort: {
      active: 'batchComplianceRequest.createdDate',
      direction: 'desc',
    },
    statusClassFunction: this.getStatusClass,
    isAdmin: this.userService.isGeoambiente,
  };

  asTypedItem = (x: any) => x as ReportTableItem;

  ngOnInit() {
    const idRouteParam = this.route.snapshot.paramMap.get('id');
    if (idRouteParam) {
      this.complianceRequestId = Number(idRouteParam);
    }

    this.filterConfig.unshift({
      key: 'batchRequestId',
      label: 'Número da Solicitação',
      type: 'number',
      defaultValue: this.complianceRequestId?.toString() ?? '',
    });
  }

  ngAfterViewInit() {
    this.searchComponent.applyFilters();
  }

  ngOnDestroy() {
    this.abortController?.abort();
  }

  private debouncedFilterLoad = debounce(
    (filters: ReportFilters) => this.loadData(filters),
    500,
  );

  private debouncedGridLoad = debounce(
    (params: Partial<ComplianceReportsFilters> = {}) => this.loadData(params),
    300,
    { leading: true },
  );

  private getStatusClass(statusCode: string | null) {
    if (statusCode == null) return null;
    const classMap: Record<
      StatusConformidade['descricao'],
      | 'chip-compliant'
      | 'chip-non-compliant'
      | 'chip-compliant-with-observations'
      | null
    > = {
      CONFORME: 'chip-compliant',
      'NÃO CONFORME': 'chip-non-compliant',
      'CONFORME COM RESSALVAS': 'chip-compliant-with-observations',
      'NÃO ANALISADO': null,
    } as const;
    return classMap[statusCode as StatusConformidade['descricao']] ?? null;
  }

  onSortChange(event: Sort) {
    this.currentSort = event;
    this.debouncedGridLoad(this.currentFilters);
  }

  onPageChange(event: PageEvent) {
    this.currentPageIndex = event.pageIndex;
    this.currentPageSize = event.pageSize;

    this.debouncedGridLoad(this.currentFilters);
  }

  handleFilters(filters: ComplianceReportsFilters) {
    // Handle state change to filter cities
    if (filters.state !== this.currentFilters.state) {
      this.geographicDataService.filterCitiesByState(filters.state || '');

      // Reset city filter when state changes
      if (filters.state !== this.currentFilters.state) {
        filters.city = undefined;
      }
    }

    this.tableComponent.paginator.firstPage(); // Reset to first page when filtering
    this.currentFilters = { ...filters };
    this.currentPageIndex = 0;
    this.debouncedFilterLoad(filters);
  }

  private getFullBackendInputFilters(
    filters: ComplianceReportsFilters,
    isPaged = true,
  ): ComplianceReportsFilters {
    const mappedSort = this.currentSort;
    return {
      batchRequestId: this.complianceRequestId
        ? Number(this.complianceRequestId)
        : undefined,
      ...filters,
      limit: isPaged ? this.currentPageSize : 500,
      cursor: isPaged ? this.currentPageIndex * this.currentPageSize : 0,
      orderBy: mappedSort?.active ? createNestedOrder(mappedSort) : undefined,
    };
  }

  private async loadData(filters: ComplianceReportsFilters) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    const requestFilters = this.getFullBackendInputFilters(filters);

    const [data, total] = await this.smartClient.client.compliance.listReports(
      requestFilters,
      {
        signal: this.abortController.signal,
      },
    );

    const complianceIdList: number[] = data
      .map((d) => d.id)
      .filter((id): id is number => id !== undefined);

    const results =
      await this.smartClient.client.compliance.findGroupedResultsByCompliance({
        complianceId: complianceIdList,
        results: ['NÃO CONFORME', 'CONFORME COM RESSALVAS'],
      });

    const getCompliantDescription = (c: Compliance) => {
      const status = c.compliant;

      if (status === 'CONFORME') return 'Conforme';
      if (status === 'NÃO CONFORME') return 'Não Conforme';
      if (status === 'CONFORME COM RESSALVAS') return 'Conforme com Ressalvas';

      return 'Não Analisado';
    };

    const mappedData: ReportTableItem[] = data.map((item) => {
      // Type assertion para incluir campos estendidos do backend
      const request =
        item.batchComplianceRequest as typeof item.batchComplianceRequest & {
          organizationName?: string | null;
          userName?: string | null;
        };

      const itemCompliant = item.compliant;
      return {
        id: item.id!,
        requestNumber: item.batchComplianceRequest.id!,
        requestDate: item.batchComplianceRequest.createdDate!.toString(),
        requester: request.userName ?? '',
        area: formatArea(item.areaHa) ?? '',
        areaIdentification: item.idArea ?? '',
        cities:
          item.municipiosJson?.map(
            (region) => `${region.mun} - ${region.uf}`,
          ) || [],
        cpfCnpj: item.document || '',
        producer: item.fileAttributes.producer || '',
        client: request.organizationName ?? '',
        areaStatus: {
          code: itemCompliant ?? 'NÃO ANALISADO',
          description: getCompliantDescription(item),
        },
        error: item.error ?? undefined,
        contract: item.fileAttributes.contract ?? '',
        car: item.fileAttributes.car ?? '',
        insurancePolicy: item.fileAttributes.insurancePolicy ?? '',
        farmName: item.fileAttributes.farmName ?? '',
        results: results.filter((r) => r.id_compliance === item.id!),
      } satisfies ReportTableItem;
    });

    this.dataSource.data = mappedData;
    this.length = total;
  }

  alert = (message: string, duration = 5000) =>
    this.snackBar.open(message, 'Fechar', { duration });

  async downloadPage() {
    const items = this.dataSource.data.filter(
      (item) => item.areaStatus?.code !== 'NÃO ANALISADO',
    );
    const len = items.length;
    if (len === 0) {
      this.alert('Nenhum relatório disponível para download nesta página.');
      return;
    }
    const alert = this.alert(
      `Baixando ${len} arquivo${len > 1 ? 's' : ''}...`,
      -1,
    );

    for await (const item of items) {
      await this.qgis.downloadComplianceReport(item.id!);
    }
    alert.dismiss();
  }

  isExpanded(element: ReportTableItem): boolean {
    return this.expandedElement === element;
  }

  onRowToggle(element: ReportTableItem): void {
    this.expandedElement = this.isExpanded(element) ? null : element;
  }

  handleSituationChipClick(element: ReportTableItem): void {
    this.dialog.open(ComplianceResultListDialog, {
      data: {
        compliant: element.areaStatus?.description || '',
        results: this.dataSource.data
          .filter((data) => data.id === element.id)[0]
          .results.map((result) => ({
            label: result.descricao_agrupamento,
            type: result.classificacao,
          })),
        observation: element.error,
      },
    });
  }

  isAdmin(): boolean {
    return this.userService.isGeoambiente;
  }

  formatDate(dateValue: unknown): string {
    if (!dateValue) return '';

    if (typeof dateValue === 'string') {
      const date = new Date(dateValue);
      return (
        date.toLocaleDateString('pt-BR') +
        ' ' +
        date.toLocaleTimeString('pt-BR')
      );
    }

    if (dateValue instanceof Date) {
      return (
        dateValue.toLocaleDateString('pt-BR') +
        ' ' +
        dateValue.toLocaleTimeString('pt-BR')
      );
    }

    return String(dateValue);
  }
}
